import { create } from 'zustand'
import type { Category, Product } from '@/types'
import { menuService } from '@/lib/menu'
import { toast } from 'sonner'

interface MenuState {
  categories: Category[]
  products: Product[]
  selectedCategory: Category | null
  isLoading: boolean
  error: string | null

  // Actions
  fetchCategories: (restaurantId: string) => Promise<void>
  fetchProducts: (restaurantId: string) => Promise<void>
  fetchProductsByCategory: (categoryId: string) => Promise<void>

  createCategory: (restaurantId: string, data: Omit<Category, 'id' | 'restaurantId'>) => Promise<Category>
  updateCategory: (categoryId: string, data: Partial<Category>) => Promise<Category>
  deleteCategory: (categoryId: string) => Promise<void>

  createProduct: (restaurantId: string, data: Omit<Product, 'id' | 'restaurantId'>) => Promise<Product>
  updateProduct: (productId: string, data: Partial<Product>) => Promise<Product>
  toggleProductAvailability: (productId: string) => Promise<void>
  deleteProduct: (productId: string) => Promise<void>

  setSelectedCategory: (category: Category | null) => void
  clearError: () => void
}

export const useMenuStore = create<MenuState>((set, get) => ({
  categories: [],
  products: [],
  selectedCategory: null,
  isLoading: false,
  error: null,

  // ========== Fetch Operations ==========
  fetchCategories: async (restaurantId: string) => {
    set({ isLoading: true, error: null })
    try {
      const categories = await menuService.getCategories(restaurantId)
      set({ categories, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento categorie'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  fetchProducts: async (restaurantId: string) => {
    set({ isLoading: true, error: null })
    try {
      const products = await menuService.getProducts(restaurantId)
      set({ products, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento prodotti'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  fetchProductsByCategory: async (categoryId: string) => {
    set({ isLoading: true, error: null })
    try {
      const products = await menuService.getProductsByCategory(categoryId)
      set({ products, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento prodotti'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  // ========== Category CRUD ==========
  createCategory: async (restaurantId: string, data) => {
    set({ isLoading: true, error: null })
    try {
      const newCategory = await menuService.createCategory(restaurantId, data)
      set(state => ({
        categories: [...state.categories, newCategory].sort((a, b) => a.displayOrder - b.displayOrder),
        isLoading: false
      }))
      toast.success('Categoria creata con successo')
      return newCategory
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nella creazione categoria'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  updateCategory: async (categoryId: string, data) => {
    set({ isLoading: true, error: null })
    try {
      const updatedCategory = await menuService.updateCategory(categoryId, data)
      set(state => ({
        categories: state.categories
          .map(c => c.id === categoryId ? updatedCategory : c)
          .sort((a, b) => a.displayOrder - b.displayOrder),
        isLoading: false
      }))
      toast.success('Categoria aggiornata con successo')
      return updatedCategory
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'aggiornamento categoria'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  deleteCategory: async (categoryId: string) => {
    set({ isLoading: true, error: null })
    try {
      await menuService.deleteCategory(categoryId)
      set(state => ({
        categories: state.categories.filter(c => c.id !== categoryId),
        products: state.products.filter(p => p.categoryId !== categoryId),
        selectedCategory: state.selectedCategory?.id === categoryId ? null : state.selectedCategory,
        isLoading: false
      }))
      toast.success('Categoria eliminata con successo')
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'eliminazione categoria'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  // ========== Product CRUD ==========
  createProduct: async (restaurantId: string, data) => {
    set({ isLoading: true, error: null })
    try {
      const newProduct = await menuService.createProduct(restaurantId, data)
      set(state => ({
        products: [...state.products, newProduct],
        isLoading: false
      }))
      toast.success('Prodotto creato con successo')
      return newProduct
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nella creazione prodotto'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  updateProduct: async (productId: string, data) => {
    set({ isLoading: true, error: null })
    try {
      const updatedProduct = await menuService.updateProduct(productId, data)
      set(state => ({
        products: state.products.map(p => p.id === productId ? updatedProduct : p),
        isLoading: false
      }))
      toast.success('Prodotto aggiornato con successo')
      return updatedProduct
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'aggiornamento prodotto'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  toggleProductAvailability: async (productId: string) => {
    try {
      const updatedProduct = await menuService.toggleProductAvailability(productId)
      set(state => ({
        products: state.products.map(p => p.id === productId ? updatedProduct : p),
      }))
      toast.success(`Prodotto ${updatedProduct.available ? 'disponibile' : 'non disponibile'}`)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel cambio disponibilità'
      toast.error(errorMessage)
      throw error
    }
  },

  deleteProduct: async (productId: string) => {
    set({ isLoading: true, error: null })
    try {
      await menuService.deleteProduct(productId)
      set(state => ({
        products: state.products.filter(p => p.id !== productId),
        isLoading: false
      }))
      toast.success('Prodotto eliminato con successo')
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'eliminazione prodotto'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  // ========== UI State ==========
  setSelectedCategory: (category) => set({ selectedCategory: category }),
  clearError: () => set({ error: null }),
}))
